import { useState } from 'react';
import { toast } from 'sonner';
import { Check, Copy } from 'lucide-react';
import { BRAND_COLOR_GROUPS } from '../data/colors';
import { TypographySection } from './TypographySection';

function contrastColor(hex: string): 'black' | 'white' {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 140 ? 'black' : 'white';
}

function Swatch({ name, hex, description }: { name: string; hex: string; description?: string }) {
  const [copied, setCopied] = useState(false);
  const textColor = contrastColor(hex);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(hex);
      setCopied(true);
      toast.success(`${hex} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="group flex flex-col rounded-md overflow-hidden border border-slate-200 hover:shadow transition-shadow text-left"
    >
      <div
        className="flex items-center justify-between px-3 h-24"
        style={{ backgroundColor: hex, color: textColor }}
      >
        <span className="text-xs font-semibold">{hex.toUpperCase()}</span>
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100" />}
      </div>
      <div className="px-3 py-2 bg-white">
        <div className="text-sm font-medium text-slate-900">{name}</div>
        {description && <div className="text-xs text-slate-500 mt-0.5">{description}</div>}
      </div>
    </button>
  );
}

export function ColorsTab() {
  return (
    <div className="space-y-8">
      {BRAND_COLOR_GROUPS.map((group) => (
        <section key={group.title}>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600 mb-3">{group.title}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {group.colors.map((c) => (
              <Swatch key={c.hex + c.name} {...c} />
            ))}
          </div>
        </section>
      ))}
      <p className="text-xs text-slate-500 pt-4">
        Palette source: <code className="px-1 rounded bg-slate-100">ATEK Brand Guidelines v2.1</code>. Click any swatch to copy the hex.
      </p>

      <div className="pt-6 border-t mt-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Typography</h2>
        <TypographySection />
      </div>
    </div>
  );
}
